import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import {
  clearOutlookOAuthReturn,
  storeOutlookOAuthReturn,
} from '../utils/email/outlookOAuthReturn';
import { useStartMicrosoftEmailOAuthMutation } from './useStartMicrosoftEmailOAuthMutation';

type ConnectOptions = {
  // The page publishes contentMeta once mounted, so the reopen after the redirect must wait
  // for it rather than reopening under a scope that is still settling.
  pageSetsContentMeta?: boolean;
  // Resume the paused email request by auto-sending a continuation once back in the chat.
  autoResume?: boolean;
};

// Shared Connect Outlook flow: remember where to return to, start the OAuth flow, and
// hand the browser to Microsoft. Used by the email draft card and the Connect Outlook card.
export function useConnectOutlook() {
  const { startOAuth, isLoading: isConnecting } = useStartMicrosoftEmailOAuthMutation();

  const connectOutlook = async (conversationId?: string, options?: ConnectOptions) => {
    // Hoisted out of the try block: the React Compiler cannot yet optimise value blocks
    // (optional chaining) inside try/catch.
    const pageSetsContentMeta = options?.pageSetsContentMeta === true;
    const autoResume = options?.autoResume === true;
    try {
      if (conversationId) {
        storeOutlookOAuthReturn({
          path: window.location.pathname + window.location.search,
          conversationId,
          pageSetsContentMeta,
          autoResume,
        });
      }
      const { authUrl } = await startOAuth();
      window.location.assign(authUrl);
    } catch (error) {
      // No redirect happened, so nothing will ever consume the stored return — clear it or it
      // fires a surprise conversation reopen on a later navigation.
      clearOutlookOAuthReturn();
      notifyAndLogError(
        'Could not connect to Outlook',
        'The connection could not be started. Please try again.',
        error,
      );
    }
  };

  return { connectOutlook, isConnecting };
}
